import React, { useState } from 'react';
import {
    Grid,
    Card,
    Title,
    Stack,
    Group,
    Box,
    Text,
    Button,
    Divider,
    Timeline,
    Badge,
    ThemeIcon,
    CopyButton,
    Tooltip,
    ActionIcon,
    Accordion,
    Paper,
} from '@mantine/core';
import { IconPackage, IconTruckDelivery, IconCalendar, IconCopy, IconCheck, IconMapPin, IconClock } from '@tabler/icons-react';
import { TrackingInformationProps } from './types';
import { formatDate, getStatusBadgeColor } from './utils';

const TrackingInformation: React.FC<TrackingInformationProps> = ({ shipment }) => {
    const [expandedEvents, setExpandedEvents] = useState<boolean>(false);

    // Default to 3 most recent events for mobile, expand to show all on click
    const displayEvents = expandedEvents ? shipment.events : shipment.events.slice(0, 3);

    // Get the status color for the badge
    const statusColor = getStatusBadgeColor(shipment.status);

    // Get event badge color
    const getEventBadgeColor = (status: string) => {
        const colorMap: Record<string, string> = {
            'Order Created': 'blue',
            'Processing At Warehouse': 'indigo',
            'Ready To Ship': 'violet',
            'Handed Over To Airline': 'yellow',
            'Arrived At Destination': 'orange',
            'Held By Customs': 'red',
            'Customs Cleared': 'teal',
            'Processing At Local Warehouse': 'cyan',
            'Delivered': 'green'
        };

        return colorMap[status] || 'gray';
    };

    return (
        <Card shadow="sm" p={{ base: "sm", sm: "lg" }} radius="md" withBorder>
            <Title order={3} mb="md" p={{ base: "h4", sm: "h3" }}>Tracking Information</Title>

            {/* Mobile-First Design */}

            {/* Quick Status Card - Mobile Priority */}
            <Paper withBorder p="sm" radius="md" mb="md" bg="gray.0" display={{ base: 'block', md: 'none' }}>
                <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={600}>Current Status</Text>
                    <Badge size="lg" color={statusColor} variant="filled">
                        {shipment.status.charAt(0).toUpperCase() + shipment.status.slice(1)}
                    </Badge>
                </Group>

                {shipment.trackingNumber && (
                    <Group justify="space-between" align="center" mb="xs">
                        <Text size="xs" c="dimmed">Tracking #</Text>
                        <Group gap="xs">
                            <Text size="xs" fw={500} style={{ fontFamily: 'monospace' }}>
                                {shipment.trackingNumber}
                            </Text>
                            <CopyButton value={shipment.trackingNumber} timeout={2000}>
                                {({ copied, copy }) => (
                                    <ActionIcon size="sm" variant="subtle" onClick={copy}>
                                        {copied ? <IconCheck size={12} color="green" /> : <IconCopy size={12} />}
                                    </ActionIcon>
                                )}
                            </CopyButton>
                        </Group>
                    </Group>
                )}

                <Group justify="space-between" align="center">
                    <Text size="xs" c="dimmed">Carrier</Text>
                    <Text size="xs" fw={500}>{shipment.carrier || 'N/A'}</Text>
                </Group>
            </Paper>

            {/* Desktop View */}
            <Box display={{ base: 'none', md: 'block' }}>
                <Grid>
                    <Grid.Col span={6}>
                        <Stack gap="md">
                            <Group>
                                <ThemeIcon color="blue" variant="light" size="md">
                                    <IconPackage size={16} />
                                </ThemeIcon>
                                <Box>
                                    <Text size="xs" c="dimmed">Tracking Number</Text>
                                    <Group gap="xs">
                                        <Text fw={500}>{shipment.trackingNumber}</Text>
                                        <CopyButton value={shipment.trackingNumber || ''} timeout={2000}>
                                            {({ copied, copy }) => (
                                                <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="right">
                                                    <ActionIcon color={copied ? 'teal' : 'gray'} onClick={copy} size="sm">
                                                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                                                    </ActionIcon>
                                                </Tooltip>
                                            )}
                                        </CopyButton>
                                    </Group>
                                </Box>
                            </Group>

                            <Group>
                                <ThemeIcon color="gray" variant="light" size="md">
                                    <IconTruckDelivery size={16} />
                                </ThemeIcon>
                                <Box>
                                    <Text size="xs" c="dimmed">Carrier</Text>
                                    <Text fw={500}>{shipment.carrier}</Text>
                                </Box>
                            </Group>

                            <Group>
                                <ThemeIcon color="gray" variant="light" size="md">
                                    <IconCalendar size={16} />
                                </ThemeIcon>
                                <Box>
                                    <Text size="xs" c="dimmed">Date Created</Text>
                                    <Text fw={500}>{formatDate(shipment.createdDate)}</Text>
                                </Box>
                            </Group>

                            {shipment.estimatedDelivery && !shipment.deliveredDate && (
                                <Group>
                                    <ThemeIcon color="blue" variant="light" size="md">
                                        <IconCalendar size={16} />
                                    </ThemeIcon>
                                    <Box>
                                        <Text size="xs" c="dimmed">Estimated Delivery</Text>
                                        <Text fw={500}>{formatDate(shipment.estimatedDelivery)}</Text>
                                    </Box>
                                </Group>
                            )}
                        </Stack>
                    </Grid.Col>

                    <Grid.Col span={6}>
                        <Box>
                            <Text size="xs" c="dimmed" mb={4}>Status</Text>
                            <Badge color={statusColor} size="lg" radius="sm" mb="md">
                                {shipment.status.charAt(0).toUpperCase() + shipment.status.slice(1)}
                            </Badge>

                            {shipment.deliveredDate && (
                                <Text size="sm">
                                    <Text span fw={600}>Delivered:</Text> {formatDate(shipment.deliveredDate)}
                                </Text>
                            )}

                            {shipment.shippedDate && !shipment.deliveredDate && (
                                <Text size="sm">
                                    <Text span fw={600}>Shipped:</Text> {formatDate(shipment.shippedDate)}
                                </Text>
                            )}
                        </Box>
                    </Grid.Col>
                </Grid>
            </Box>

            {/* Mobile Tracking Details Accordion */}
            <Box display={{ base: 'block', md: 'none' }} mt="md">
                <Accordion defaultValue="details">
                    <Accordion.Item value="details">
                        <Accordion.Control icon={<IconMapPin size={16} />}>
                            Tracking Details
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="xs">
                                <Group justify="space-between">
                                    <Text size="xs" c="dimmed">Created</Text>
                                    <Text size="xs">{formatDate(shipment.createdDate)}</Text>
                                </Group>
                                {shipment.shippedDate && (
                                    <Group justify="space-between">
                                        <Text size="xs" c="dimmed">Shipped</Text>
                                        <Text size="xs">{formatDate(shipment.shippedDate)}</Text>
                                    </Group>
                                )}
                                {shipment.deliveredDate && (
                                    <Group justify="space-between">
                                        <Text size="xs" c="dimmed">Delivered</Text>
                                        <Text size="xs">{formatDate(shipment.deliveredDate)}</Text>
                                    </Group>
                                )}
                                {shipment.estimatedDelivery && !shipment.deliveredDate && (
                                    <Group justify="space-between">
                                        <Text size="xs" c="dimmed">Est. Delivery</Text>
                                        <Text size="xs">{formatDate(shipment.estimatedDelivery)}</Text>
                                    </Group>
                                )}
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            </Box>

            <Divider my="md" label="Tracking History" labelPosition="center" />

            {/* Timeline - Responsive */}
            <Timeline
                active={Math.min(displayEvents.length - 1, displayEvents.length)}
                bulletSize={24}
                lineWidth={2}
                mb={displayEvents.length < shipment.events.length ? 'xs' : 'md'}
            >
                {displayEvents.map((event, index) => (
                    <Timeline.Item
                        key={index}
                        bullet={
                            <ThemeIcon
                                radius="xl"
                                color={index === 0 ? statusColor : getEventBadgeColor(event.status)}
                            >
                                <IconCheck />
                            </ThemeIcon>
                        }
                        title={
                            <Group gap="xs" wrap="nowrap">
                                <Text fw={500} fz={{ base: 'sm', md: 'md' }}>{event.status}</Text>
                                <Text size="xs" c="dimmed">
                                    {formatDate(event.date).split(' at ')[0]}
                                </Text>
                            </Group>
                        }
                    >
                        <Stack gap="xs">
                            <Group gap="xs" align="center">
                                <IconMapPin size={14} style={{ color: 'var(--mantine-color-dimmed)' }} />
                                <Text color="dimmed" fz={{ base: 'xs', md: 'sm' }}>
                                    {event.location}
                                </Text>
                            </Group>
                            <Group gap="xs" align="center">
                                <IconClock size={14} style={{ color: 'var(--mantine-color-dimmed)' }} />
                                <Text color="dimmed" fz={{ base: 'xs', md: 'sm' }}>
                                    {formatDate(event.date).split(' at ')[1] || 'Time not available'}
                                </Text>
                            </Group>
                            <Text fz={{ base: 'xs', md: 'sm' }} mt={4}>
                                {event.description}
                            </Text>
                        </Stack>
                    </Timeline.Item>
                ))}
            </Timeline>

            {/* Show "View More" button if there are hidden events */}
            {shipment.events.length > 3 && (
                <Button
                    variant="subtle"
                    size="sm"
                    fullWidth
                    onClick={() => setExpandedEvents(!expandedEvents)}
                    mt="xs"
                >
                    {expandedEvents ? 'View Less' : `View ${shipment.events.length - 3} More Events`}
                </Button>
            )}

            {/* Quick Actions - Mobile */}
            {/* <Box mt="md" display={{ base: 'block', md: 'none' }}>
                <Group grow>
                    <Button variant="light" size="sm">
                        Share Tracking
                    </Button>
                    <Button variant="light" size="sm">
                        Get Updates
                    </Button>
                </Group>
            </Box> */}
        </Card>
    );
};

export default TrackingInformation;
